import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Check, Lock, Sparkles, Palette } from 'lucide-react';
import { themes, getTheme, applyTheme, getSavedTheme, getUnlockedThemes, getLockedThemes } from '../themes';
import { toast } from 'sonner';

const ThemeSelector = ({ open, onClose, isPremium, onUpgrade }) => {
  const [selectedTheme, setSelectedTheme] = useState(getSavedTheme());
  const [previewTheme, setPreviewTheme] = useState(null);

  useEffect(() => {
    if (open) {
      setSelectedTheme(getSavedTheme());
    }
  }, [open]);

  // Preview theme on hover
  const handlePreview = (themeId) => {
    if (!previewTheme) {
      setPreviewTheme(getSavedTheme()); // Store current theme
    }
    applyTheme(themeId);
  };

  // Restore original theme on mouse leave
  const handlePreviewEnd = () => {
    if (previewTheme) {
      applyTheme(previewTheme);
      setPreviewTheme(null);
    }
  };

  const handleSelectTheme = (theme) => {
    // Check if theme is locked (IAP)
    if (theme.isLocked) {
      toast.info(`${theme.name} - Coming soon! $${theme.price}`);
      return;
    }

    // Check if premium is required
    if (theme.isPremium && !isPremium) {
      toast.error('Premium required for this theme');
      onUpgrade();
      return;
    }

    // Apply theme
    applyTheme(theme.id);
    setSelectedTheme(theme.id);
    setPreviewTheme(null);
    toast.success(`${theme.name} applied!`);
  };

  const handleClose = () => {
    // Restore to selected theme on close
    applyTheme(selectedTheme);
    setPreviewTheme(null);
    onClose();
  };

  const unlockedThemes = getUnlockedThemes(isPremium);
  const lockedThemes = getLockedThemes();

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent 
        data-testid="theme-selector-modal" 
        className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto"
        onMouseLeave={handlePreviewEnd}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading flex items-center gap-2">
            <Palette className="w-7 h-7 text-primary" />
            Customize Your Theme
          </DialogTitle>
          <p className="text-muted-foreground mt-2">
            {isPremium ? 'Choose your adventure style' : 'Upgrade to unlock all themes'}
          </p>
        </DialogHeader>

        {/* Theme Grid */}
        <div className="space-y-4 mt-4">
          {/* Available Themes */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">
              {isPremium ? 'Available Themes' : 'Free Theme'}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {Object.values(themes)
                .filter(t => !t.isLocked && (!t.isPremium || isPremium))
                .map((theme) => (
                  <ThemeCard
                    key={theme.id}
                    theme={theme}
                    isSelected={selectedTheme === theme.id}
                    isPremium={isPremium}
                    onSelect={() => handleSelectTheme(theme)}
                    onPreview={() => handlePreview(theme.id)}
                  />
                ))}
            </div>
          </div>

          {/* Premium Themes (shown to free users as locked) */}
          {!isPremium && (
            <div className="space-y-3 mt-6">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  Premium Themes
                </h3>
                <Button
                  onClick={onUpgrade}
                  size="sm"
                  className="h-7 text-xs rounded-full"
                >
                  Unlock All
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {Object.values(themes)
                  .filter(t => t.isPremium && !t.isLocked)
                  .map((theme) => (
                    <ThemeCard
                      key={theme.id}
                      theme={theme}
                      isSelected={false}
                      isPremium={false}
                      isLocked={true}
                      onSelect={() => handleSelectTheme(theme)}
                      onPreview={() => handlePreview(theme.id)}
                    />
                  ))}
              </div>
            </div>
          )}

          {/* Coming Soon - IAP Themes */}
          {lockedThemes.length > 0 && (
            <div className="space-y-3 mt-6">
              <h3 className="text-sm font-semibold text-muted-foreground">
                Coming Soon
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {lockedThemes.map((theme) => (
                  <ThemeCard
                    key={theme.id}
                    theme={theme}
                    isSelected={false}
                    isPremium={isPremium}
                    isLocked={true}
                    isIAP={true}
                    onSelect={() => handleSelectTheme(theme)}
                    onPreview={() => {}}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <Button
          onClick={handleClose}
          variant="ghost"
          className="w-full mt-4 text-muted-foreground"
        >
          Done
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// Individual Theme Card Component
const ThemeCard = ({ theme, isSelected, isPremium, isLocked, isIAP, onSelect, onPreview }) => {
  return (
    <Card
      data-testid={`theme-card-${theme.id}`}
      className={`cursor-pointer transition-all duration-200 overflow-hidden ${
        isSelected 
          ? 'ring-2 ring-primary border-primary' 
          : 'border-border hover:border-primary/50'
      } ${isLocked ? 'opacity-75' : ''}`}
      onClick={onSelect}
      onMouseEnter={!isLocked ? onPreview : undefined}
    >
      {/* Theme Preview Colors */}
      <div 
        className="h-16 relative"
        style={{ 
          background: theme.backgroundImage || theme.colors.background,
          borderBottom: `3px solid ${theme.colors.primary}`
        }}
      >
        {/* Color dots preview */}
        <div className="absolute bottom-2 left-2 flex gap-1">
          <div 
            className="w-4 h-4 rounded-full border border-white/30"
            style={{ backgroundColor: theme.colors.primary }}
          />
          <div 
            className="w-4 h-4 rounded-full border border-white/30"
            style={{ backgroundColor: theme.colors.accent }}
          />
          <div 
            className="w-4 h-4 rounded-full border border-white/30"
            style={{ backgroundColor: theme.colors.card }}
          />
        </div>

        {/* Lock/Check indicator */}
        {isLocked && (
          <div className="absolute top-2 right-2 bg-black/50 rounded-full p-1">
            <Lock className="w-3 h-3 text-white" />
          </div>
        )}
        {isSelected && !isLocked && (
          <div className="absolute top-2 right-2 bg-primary rounded-full p-1">
            <Check className="w-3 h-3 text-primary-foreground" />
          </div>
        )}

        {/* IAP Price */}
        {isIAP && theme.price > 0 && (
          <div className="absolute top-2 left-2 bg-black/50 rounded-full px-2 py-0.5">
            <span className="text-xs text-white font-medium">${theme.price}</span>
          </div>
        )}
      </div>

      <CardContent className="p-3">
        <p className="font-semibold text-sm text-foreground truncate">
          {theme.name}
        </p>
        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
          {theme.description}
        </p>
        {!theme.isPremium && (
          <span className="text-xs text-primary mt-1 inline-block">Free</span>
        )}
      </CardContent>
    </Card>
  );
};

export default ThemeSelector;
